/**
 * FASE 4.2 — UserMenu (header dropdown post-login)
 * Mostra "Ciao [Nome] ▼" con menu di accesso rapido alle sezioni Area Cliente
 * tramite drawer (no navigate).
 */
import { useState, useRef, useEffect } from "react";
import { User, ChevronDown, FileText, Briefcase, FolderOpen, Bell, Settings, LayoutDashboard, LogOut } from "lucide-react";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useAuthModals } from "@/components/auth/AuthModalsHost";

export default function UserMenu({ inline = false }) {
  const { account, logout } = useClientAuth();
  const { openAreaCliente } = useAuthModals();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!account) return null;

  const name = account.profilo_titolare?.anagrafica?.nome
    || account.email?.split("@")[0]
    || "Cliente";

  const items = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "polizze", label: "Le mie polizze", icon: FileText },
    { key: "preventivi", label: "I miei preventivi", icon: Briefcase },
    { key: "documenti", label: "I miei documenti", icon: FolderOpen },
    { key: "notifiche", label: "Notifiche", icon: Bell },
    { key: "profilo", label: "Profilo", icon: User },
    { key: "impostazioni", label: "Impostazioni", icon: Settings },
  ];

  return (
    <div ref={ref} className="relative" data-testid="user-menu">
      <button onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-all border ${
          inline ? "border-zinc-200 text-zinc-700 hover:bg-zinc-50" : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
        }`}
        data-testid="user-menu-trigger">
        <User className="w-3.5 h-3.5" />
        <span className="font-medium">Ciao {name}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-zinc-100 overflow-hidden z-[60]"
          data-testid="user-menu-dropdown">
          <div className="px-4 py-3 bg-emerald-50/40 border-b border-zinc-100">
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium">Account</p>
            <p className="text-sm font-medium text-zinc-900 truncate">{account.email}</p>
          </div>
          <ul className="py-1">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <li key={it.key}>
                  <button
                    onClick={() => { openAreaCliente(it.key); setOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-zinc-700 hover:bg-emerald-50 hover:text-emerald-800 text-left"
                    data-testid={`user-menu-item-${it.key}`}
                  >
                    <Icon className="w-4 h-4 text-zinc-500" />
                    {it.label}
                  </button>
                </li>
              );
            })}
            <li className="border-t border-zinc-100 mt-1">
              <button onClick={async () => { await logout(); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 text-left"
                data-testid="user-menu-logout">
                <LogOut className="w-4 h-4" /> Esci
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
